import { Button, Flex, Form, message, Spin, Space, Card } from "antd";
import { ArrowLeftOutlined, EditOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  useGetFormFieldsQuery,
  usePostAusregSubmissionMutation,
  usePostGetKycOrderMutation,
} from "../api/apiSlice";
import Container from "../components/Container";
import LogoImage from "../components/LogoImage";
import SimCardInfo from "../components/SimCardInfo";
import FormInput from "../components/FormInput";
import ConfirmButton from "../components/ConfirmButton";
import Title from "../components/Typography/Title";
import Text from "../components/Typography/Text";
import MenuButton from "../components/MenuButton";
import { colors } from "../style";
const FORM_OPTIONS = [
  { id: "passport-detail", label: "Enter Details Manually" },
  { id: "passport-upload", label: "Upload Passport Page" },
] as const;

type AusregFormId = (typeof FORM_OPTIONS)[number]["id"];


const UPLOAD_KYC_MEDIA_URL = "https://bf6q09zw9f.execute-api.ap-southeast-1.amazonaws.com/test/api/usim/customer/manage?action=uploadKycPhoto"
export default function AusregPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlOrderNumber = searchParams.get("orderNumber");
  const urlId = searchParams.get("id");
  
  const [product, setProduct] = useState<any>(location.state?.product || null);
  const [orderItems, setOrderItems] = useState<any[]>([]);

  const stateOrderNumber = location.state?.orderNumber;

  const [messageApi, contextHolder] = message.useMessage();
  const [selectedFormId, setSelectedFormId] =
    useState<AusregFormId>("passport-detail");
  const { data, error, isLoading } = useGetFormFieldsQuery({
    formId: selectedFormId,
  });
  const [submitAusreg] = usePostAusregSubmissionMutation();
  const [getKycOrder, { isLoading: isKycLoading }] = usePostGetKycOrderMutation();

  const [orderNumber, setOrderNumber] = useState(stateOrderNumber || urlOrderNumber || "");

  useEffect(() => {
    if (urlOrderNumber) {
      handleGetDetails(urlOrderNumber, urlId);
    }
  }, [urlOrderNumber]);

  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (product?.kycDetails) {
      setFormData((prev) => ({
        ...prev,
        ...product.kycDetails,
      }));
    } else {
      setFormData({});
    }
  }, [product]);

  function handleSetFormData(key: string, value: string) {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }

  const handleGetDetails = (orderNo: string, preselectId?: string | null) => {
    if (!orderNo) {
      messageApi.error("Please enter an order number");
      return;
    }
    setProduct(null);
    getKycOrder({ orderNumber: orderNo })
      .unwrap()
      .then((data) => {
        const filteredData = (data || []).filter((item: any) => 
          item.type?.toLowerCase().includes("australia") || item.type?.toLowerCase().includes("au")
        );
        setOrderItems(filteredData);
        if (preselectId) {
          const matchedProduct = filteredData.find((item: any) => item.shopId === preselectId);
          if (matchedProduct) {
            setProduct(matchedProduct);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to get KYC order", err);
        messageApi.error("Failed to load order details");
      });
  };

  async function handleFinish() {
    if (!orderNumber) {
      messageApi.error("Please enter an order number");
      return;
    }

    const uploadFields = data?.fields.filter(t => t.type.startsWith("upload")) ?? [];
    let uploadFieldKey: string | undefined = undefined;
    if (uploadFields.length > 0) {
      uploadFieldKey = uploadFields[0].name;
    }

    let uploadPhotoUrl = "";
    if (uploadFields.length > 0) {
      if (file === null) {
        if (uploadFields.some(t => t.required)) {
          messageApi.error("Please check your fields and try again");
          return;
        }
      } else {
        try {
          messageApi.loading("Uploading file...");
          const resGenUrl = await fetch(UPLOAD_KYC_MEDIA_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              orderNumber: orderNumber,
              filename: file.name
            }),
          });

          if (!resGenUrl.ok) {
            throw new Error("Failed to generate upload URL");
          }

          const result = await resGenUrl.json();
          const resUpload = await fetch(result.uploadUrl, {
            method: "PUT",
            body: file,
          });

          if (!resUpload.ok) {
            throw new Error("Failed to upload file to S3");
          }
          // console.log("result of file upload", resUpload)

          uploadPhotoUrl = result.objectId;
          messageApi.destroy();
        } catch (ex) {
          messageApi.destroy();
          messageApi.error("Error uploading file. If this issue persists, please contact our support.");
          return;
        }
      }
    }

    const fieldNames = new Set(data?.fields.map((field) => field.name) ?? []);
    const kycDetails = Object.fromEntries(
      Object.entries(formData).filter(([key]) => fieldNames.has(key))
    );

    if (uploadPhotoUrl.length > 0 && uploadFieldKey) {
      kycDetails[uploadFieldKey] = uploadPhotoUrl;
    }

    const req = {
      OrderNumber: orderNumber,
      shopId: product?.shopId || urlId,
      kycDetails,
    };
    // console.info("Ausreg submit request", req);

    messageApi.loading("Submitting...");
    try {
      await submitAusreg(req).unwrap();
      messageApi.destroy();
      messageApi.success("Submitted successfully");
      setIsSubmitted(true);
      if (orderNumber) {
        handleGetDetails(orderNumber);
      }
    } catch (err: any) {
      messageApi.destroy();
      console.error("Failed to submit ausreg", err);
      if (err?.status === 400) {
        messageApi.error(err.data?.message ?? "Failed to submit");
      } else {
        messageApi.error("Failed to submit");
      }
    }
  }

  function handleSelectFormId(formId: AusregFormId) {
    setSelectedFormId(formId);
    setIsSubmitted(false);
  }




  function renderFormIdTabs() {
    return (
      <Flex gap={8} align="center" style={{ width: "100%" }}>
        {FORM_OPTIONS.map((option) => {
          const isSelected = option.id === selectedFormId;
          return (
            <Button
              key={option.id}
              size="small"
              type={isSelected ? "primary" : "default"}
              onClick={() => handleSelectFormId(option.id)}
              aria-pressed={isSelected}
              style={{
                flex: 1,
                height: 38,
                paddingBlock: 0,
              }}
            >
              {option.label}
            </Button>
          );
        })}
      </Flex>
    );
  }

  return (
    <Container>
      {contextHolder}
      <LogoImage />
      {error && <Title level={3}>Failed to load form</Title>}
      {(isLoading || isKycLoading) && (
        <Flex align="center" justify="center" flex={1}>
          <Spin />
        </Flex>
      )}
      {!error && data && (
        <>
          <SimCardInfo
            title={data.title ?? "Australia Registration"}
            imageUrl={
              data.image && data.image !== ""
                ? data.image
                : "https://echo-assets.s3.ap-southeast-1.amazonaws.com/simply-map.png"
            }
            description={data.description ?? ""}
          />
          {isSubmitted ? (
            <Flex vertical gap={2} justify="center">
              <Title level={3}>Thank you for registering</Title>
              <Text>Your details have been submitted</Text>
              <Button type="primary" onClick={() => setIsSubmitted(false)} style={{marginTop: 16}}>
                Back to product
              </Button>
            </Flex>
          ) : (
            <div style={{ flex: 1, width: "100%" }}>
              {!product && orderItems.length === 0 && (
                <Space direction="vertical" style={{ width: "100%" }}>
                  <FormInput
                    field={{
                      label: "Order Number",
                      type: "text",
                      name: "orderNumber",
                      maxLength: 1024,
                      prefilledValue: null,
                      required: true,
                    }}
                    value={orderNumber}
                    onChange={setOrderNumber}
                  />
                  <Button type="primary" block onClick={() => handleGetDetails(orderNumber)}>
                    Get details
                  </Button>
                </Space>
              )}

              {!product && orderItems.length > 0 && (
                <Space direction="vertical" style={{ width: "100%", marginTop: 16 }}>
                  <Text strong>Select a Product for KYC</Text>
                  {orderItems.map((item: any, idx: number) => {
                    const hasKyc = item.kycDetails && Object.keys(item.kycDetails).length > 0;
                    return (
                      <Card 
                        key={item.shopId || idx} 
                        size="small" 
                        style={{ width: "100%", cursor: "pointer", marginBottom: 8, backgroundColor: colors.secondary }}
                        onClick={() => setProduct(item)}
                      >
                        <Flex justify="space-between" align="center" style={{ width: "100%" }}>
                          <Text strong>#{idx + 1}: ICCID: {item.iccid || "N/A"}</Text>
                          <Button 
                            style={{ backgroundColor: "white" }} 
                            icon={<EditOutlined />} 
                            size="small"
                          >
                            {hasKyc ? "Update Kyc details" : "Kyc Incomplete"}
                          </Button>
                        </Flex>
                      </Card>
                    );
                  })}
                  <Button style={{ marginTop: 16 }} block onClick={() => setOrderItems([])}>
                    Back to Order details
                  </Button>
                </Space>
              )}

              {product && (
                <Form onFinish={handleFinish} style={{ width: "100%" }}>
                  <div style={{ marginBottom: 16, padding: 12, backgroundColor: colors.secondary, borderRadius: 8 }}>
                    <Flex justify="space-between" align="center">
                      <div>
                        <Text strong>ICCID: {product.iccid || "N/A"}</Text>
                      </div>
                      {/* <Button size="small" onClick={() => setProduct(null)} style={{background: "#f9f9f9"}}>
                        <ArrowLeftOutlined/>
                        Go Back
                      </Button> */}
                    </Flex>
                  </div>
                  <Form.Item style={{ marginTop: 0, marginBottom: 16 }}>
                    {renderFormIdTabs()}
                  </Form.Item>
                  {data.fields.map((field) => (
                    <Form.Item key={field.name}>
                      <FormInput
                        field={field}
                        value={formData[field.name] ?? ""}
                        onChange={(s) => handleSetFormData(field.name, s)}
                        setFile={setFile}
                      />
                    </Form.Item>
                  ))}
                  <ConfirmButton htmlType="submit" />
                </Form>
              )}
            </div>
          )}
        </>
      )}
    </Container>
  );
}
